import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["matters"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      caseExpenseList: build.query<
        CaseExpenseListApiResponse,
        CaseExpenseListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/`,
          params: {
            expense_type: queryArg.expenseType,
            payee_type: queryArg.payeeType,
            status: queryArg.status,
          },
        }),
        providesTags: ["matters"],
      }),
      caseExpenseCreate: build.mutation<
        CaseExpenseCreateApiResponse,
        CaseExpenseCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/`,
          method: "POST",
          body: queryArg.createCaseExpense,
        }),
        invalidatesTags: ["matters"],
      }),
      caseExpenseRetrieve: build.query<
        CaseExpenseRetrieveApiResponse,
        CaseExpenseRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/${queryArg.id}/`,
        }),
        providesTags: ["matters"],
      }),
      caseExpenseUpdate: build.mutation<
        CaseExpenseUpdateApiResponse,
        CaseExpenseUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateCaseExpense,
        }),
        invalidatesTags: ["matters"],
      }),
      caseExpenseDestroy: build.mutation<
        CaseExpenseDestroyApiResponse,
        CaseExpenseDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["matters"],
      }),
      caseExpenseLinkBlob: build.mutation<
        CaseExpenseLinkBlobApiResponse,
        CaseExpenseLinkBlobApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/${queryArg.id}/blobs/`,
          method: "POST",
          body: queryArg.createCaseExpenseBlob,
        }),
        invalidatesTags: ["matters"],
      }),
      caseExpenseUnlinkBlob: build.mutation<
        CaseExpenseUnlinkBlobApiResponse,
        CaseExpenseUnlinkBlobApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-expenses/${queryArg.id}/blobs/${queryArg.blobId}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["matters"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type CaseExpenseListApiResponse = /** status 200  */ ReadCaseExpense[];
export type CaseExpenseListApiArg = {
  /** Filter by expense type (POLICE, MEDICAL, MAIL, OTHER) */
  expenseType?: "MAIL" | "MEDICAL" | "OTHER" | "POLICE";
  matterId: string;
  /** Filter by payee type */
  payeeType?: "CONTACT" | "COVERAGE" | "POLICE_DEPARTMENT" | "PROVIDER";
  /** Filter by status (UNPAID, SENT, PAID, CANCELLED) */
  status?: "CANCELLED" | "PAID" | "SENT" | "UNPAID";
};
export type CaseExpenseCreateApiResponse = /** status 201  */ ReadCaseExpense;
export type CaseExpenseCreateApiArg = {
  matterId: string;
  createCaseExpense: CreateCaseExpense;
};
export type CaseExpenseRetrieveApiResponse = /** status 200  */ ReadCaseExpense;
export type CaseExpenseRetrieveApiArg = {
  id: string;
  matterId: string;
  pk: string;
};
export type CaseExpenseUpdateApiResponse = /** status 200  */ ReadCaseExpense;
export type CaseExpenseUpdateApiArg = {
  id: string;
  matterId: string;
  patchedUpdateCaseExpense: UpdateCaseExpense;
};
export type CaseExpenseDestroyApiResponse = unknown;
export type CaseExpenseDestroyApiArg = {
  id: string;
  matterId: string;
};
export type CaseExpenseLinkBlobApiResponse = unknown;
export type CaseExpenseLinkBlobApiArg = {
  id: string;
  matterId: string;
  createCaseExpenseBlob: CreateCaseExpenseBlob;
};
export type CaseExpenseUnlinkBlobApiResponse = unknown;
export type CaseExpenseUnlinkBlobApiArg = {
  blobId: string;
  id: string;
  matterId: string;
  pk: string;
};
export type CaseExpenseType = "POLICE" | "MEDICAL" | "MAIL" | "OTHER";
export type CaseExpenseStatus = "UNPAID" | "SENT" | "PAID" | "CANCELLED";
export type PaymentMethod = "CREDIT_CARD" | "CHEQUE" | "FIRM_PAYS";
export type LabeledPhone = {
  /** Label like 'mobile', 'home', 'work', 'main', 'fax' */
  label: string;
  /** Phone number */
  value: string;
};
export type LabeledEmail = {
  /** Label like 'home', 'work', 'other' */
  label: string;
  /** Email address */
  value: string;
};
export type LabeledPostalAddress = {
  /** Label like 'home', 'work', 'billing', 'shipping' */
  label: string;
  /** Street address (can include multiple lines) */
  street?: string | null;
  /** City */
  city?: string | null;
  /** State/Province */
  state?: string | null;
  /** Postal/ZIP code */
  postal_code?: string | null;
  /** Country */
  country?: string | null;
};
export type SimpleContactInfo = {
  /** Organization/company name */
  organization_name?: string | null;
  /** First name (for individuals) */
  given_name?: string | null;
  /** Last name (for individuals) */
  family_name?: string | null;
  /** Phone numbers with labels (mobile, home, work, main, fax) */
  phone_numbers?: LabeledPhone[];
  /** Email addresses with labels (home, work, other) */
  email_addresses?: LabeledEmail[];
  /** Postal addresses with labels (home, work, billing, shipping) */
  postal_addresses?: LabeledPostalAddress[];
  /** Additional notes about this contact */
  note?: string | null;
};
export type ContactPayee = {
  type?: "CONTACT";
  contact_info: SimpleContactInfo;
};
export type CoverageCategory =
  | "FIRST_PARTY"
  | "FIRST_PARTY_HEALTH"
  | "THIRD_PARTY"
  | "NOT_LIABLE_INVOLVED_PARTY"
  | "UNCLEAR"
  | "PROPERTY"
  | "WORKERS_COMPENSATION";
export type CoveragePayee = {
  type?: "COVERAGE";
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory;
};
export type PoliceDepartmentPayee = {
  type?: "POLICE_DEPARTMENT";
  id: string | string;
  name: string;
};
export type ProviderPayee = {
  type?: "PROVIDER";
  id: string | string;
  name: string;
};
export type BlobSummary = {
  id: string | string;
  name: string;
};
export type ReadCaseExpense = {
  id: string | string;
  matter_id: string | string;
  expense_type: CaseExpenseType;
  status: CaseExpenseStatus;
  payment_method?: PaymentMethod;
  payment_method_identifier?: string | null;
  payment_date?: string | null;
  amount: string;
  reason?: string | null;
  payee:
    | ({
        type: "CONTACT";
      } & ContactPayee)
    | ({
        type: "COVERAGE";
      } & CoveragePayee)
    | ({
        type: "POLICE_DEPARTMENT";
      } & PoliceDepartmentPayee)
    | ({
        type: "PROVIDER";
      } & ProviderPayee);
  blobs?: BlobSummary[];
  created_at: string;
  modified_at: string;
};
export type PayeeType =
  | "PROVIDER"
  | "COVERAGE"
  | "POLICE_DEPARTMENT"
  | "CONTACT";
export type CreateCaseExpense = {
  expense_type: CaseExpenseType;
  payee_type: PayeeType;
  /** UUID for Provider/Coverage/PoliceDepartment (required unless payee_type is CONTACT) */
  payee_id?: (string | string) | null;
  /** Contact information when payee_type is CONTACT */
  contact_info?: SimpleContactInfo;
  amount: string;
  payment_date?: string | null;
  payment_method?: PaymentMethod;
  /** Identifier for payment method (e.g., last 4 digits of CC, cheque number) */
  payment_method_identifier?: string | null;
  reason?: string | null;
  status?: CaseExpenseStatus;
};
export type UpdateCaseExpense = {
  expense_type?: CaseExpenseType;
  status?: CaseExpenseStatus;
  payment_method?: PaymentMethod;
  /** Identifier for payment method (e.g., last 4 digits of CC, cheque number) */
  payment_method_identifier?: string | null;
  payment_date?: string | null;
  amount?: string | null;
  reason?: string | null;
};
export type CreateCaseExpenseBlob = {
  blob_id: string | string;
};
export const {
  useCaseExpenseListQuery,
  useCaseExpenseCreateMutation,
  useCaseExpenseRetrieveQuery,
  useCaseExpenseUpdateMutation,
  useCaseExpenseDestroyMutation,
  useCaseExpenseLinkBlobMutation,
  useCaseExpenseUnlinkBlobMutation,
} = injectedRtkApi;
